import type { LuggageTypeEntry, SpecialLuggageBaseValues } from '../engine/types'
import { DECIMAL_INPUT_PATTERN } from '@/scopes/common/components/FormElements/utils/decimalPatternUtils'

export interface PetValues extends SpecialLuggageBaseValues {
  animalType: string
  breed: string
  // isFlight fields:
  height: string
  width: string
  length: string
  weight: string
  transportationArea: string
  containerPetType: string
  // hasCarRental fields:
  petHeight: string
  petLength: string
  petWeight: string
  // always:
  termsAndConditions: boolean
}

interface PetEntryLinks {
  termsAndConditions?: string
}

export function createPetEntry({
  isFlight,
  hasCarRental,
  showTransferInfo,
  links,
}: {
  isFlight: boolean
  hasCarRental: boolean
  showTransferInfo: boolean
  links?: PetEntryLinks
}): LuggageTypeEntry<PetValues> {
  const { termsAndConditions = '' } = links ?? {}

  return {
    schema: {
      defaultValues: {
        animalType: '',
        breed: '',
        height: '',
        width: '',
        length: '',
        weight: '',
        transportationArea: '',
        containerPetType: '',
        petHeight: '',
        petLength: '',
        petWeight: '',
        termsAndConditions: false,
      },
      sections: [
        {
          id: 'petTransferInfo',
          visibleWhen: () => isFlight && showTransferInfo,
          fields: [
            {
              kind: 'banner',
              messageKey: 'booking_details_special_luggage_pet_transfer_info',
            },
          ],
        },
        {
          id: 'petBasic',
          fields: [
            {
              kind: 'text',
              name: 'animalType',
              labelKey: 'booking_details_special_luggage_animal_type_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_animal_type_required_error',
              pattern: '^[a-zA-Z\\s]+$',
              patternErrorKey: 'booking_details_special_luggage_animal_type_pattern_error',
            },
            {
              kind: 'text',
              name: 'breed',
              labelKey: 'booking_details_special_luggage_breed_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_breed_required_error',
              pattern: '^[a-zA-Z\\s]+$',
              patternErrorKey: 'booking_details_special_luggage_breed_pattern_error',
            },
          ],
        },
        {
          id: 'petFlightDetails',
          visibleWhen: () => isFlight,
          fields: [
            {
              kind: 'dropdown',
              name: 'transportationArea',
              labelKey: 'booking_details_special_luggage_transportation_area_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_transportation_area_required_error',
              options: [
                { value: 'cabin', labelKey: 'booking_details_special_luggage_transportation_area_cabin' },
                { value: 'hold-luggage', labelKey: 'booking_details_special_luggage_transportation_area_hold_luggage' },
              ],
            },
            {
              kind: 'dropdown',
              name: 'containerPetType',
              labelKey: 'booking_details_special_luggage_container_type_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_container_type_required_error',
              options: [
                { value: 'bag', labelKey: 'booking_details_special_luggage_container_type_bag' },
                { value: 'kennel', labelKey: 'booking_details_special_luggage_container_type_kennel' },
              ],
            },
          ],
        },
        {
          id: 'measureHelpText',
          visibleWhen: () => isFlight,
          fields: [
            {
              kind: 'paragraph',
              messageKey: 'booking_details_special_luggage_measure_help_text',
            },
          ],
        },
        {
          id: 'containerDimensionsRow1',
          layout: 'row',
          titleKey: 'booking_details_special_luggage_container_measurements_title',
          visibleWhen: () => isFlight,
          fields: [
            {
              kind: 'text',
              name: 'width',
              labelKey: 'booking_details_special_luggage_width_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
            {
              kind: 'text',
              name: 'height',
              labelKey: 'booking_details_special_luggage_height_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
        },
        {
          id: 'containerDimensionsRow2',
          layout: 'row',
          visibleWhen: () => isFlight,
          fields: [
            {
              kind: 'text',
              name: 'length',
              labelKey: 'booking_details_special_luggage_length_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
            {
              kind: 'text',
              name: 'weight',
              labelKey: 'booking_details_special_luggage_weight_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_dimensions_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
        },
        {
          id: 'petMeasurementsRow1',
          layout: 'row',
          titleKey: 'booking_details_special_luggage_pet_measurements_title',
          visibleWhen: () => hasCarRental,
          fields: [
            {
              kind: 'text',
              name: 'petHeight',
              labelKey: 'booking_details_special_luggage_pet_height_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_pet_measurements_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
            {
              kind: 'text',
              name: 'petLength',
              labelKey: 'booking_details_special_luggage_pet_length_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_pet_measurements_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
        },
        {
          id: 'petMeasurementsRow2',
          layout: 'row',
          visibleWhen: () => hasCarRental,
          fields: [
            {
              kind: 'text',
              name: 'petWeight',
              labelKey: 'booking_details_special_luggage_pet_weight_label',
              required: true,
              requiredErrorKey: 'booking_details_special_luggage_pet_measurements_required_error',
              pattern: DECIMAL_INPUT_PATTERN,
              patternErrorKey: 'booking_details_special_luggage_numeric_pattern_error',
            },
          ],
        },
        {
          id: 'termsAndConditions',
          fields: [
            {
              kind: 'checkbox',
              name: 'termsAndConditions',
              labelKey: 'booking_details_special_luggage_terms_and_conditions_label',
              validationMessageKey: 'booking_details_special_luggage_terms_and_conditions_required_error',
              links: {
                termsAndConditionsLink: {
                  labelKey: 'booking_details_special_luggage_terms_and_conditions_link_label',
                  href: termsAndConditions,
                },
              },
            },
          ],
        },
      ],
    },
  }
}
